/* ----------------------------------------------------------------------------
 * judgeme-carousel.js
 * Robust carousel transform for the Judge.me "revamp" reviews widget.
 *
 * Judge.me's current widget (jdgm-widget-revamp) uses a JS-driven masonry
 * grid: a <section class="jdgm-masonry-grid"> wraps a container that holds
 * .jdgm-masonry-grid__masonry-item children, each absolutely positioned by
 * Judge.me's masonry script with computed top/left/transform values.
 *
 * To turn this into a horizontal scroll-snap row we have to:
 *   1. Find the masonry container.
 *   2. Force it to `display: flex; overflow-x: auto`.
 *   3. Neutralize each item's `position: absolute`, `top`, `left`, and
 *      `transform` — otherwise the masonry script's positioning still wins.
 *   4. Re-neutralize whenever Judge.me re-runs masonry (sort/filter/page).
 *
 * Loaded only on product pages from layout/theme.liquid.
 * -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var DEBUG = true; // flip to false once verified
  var INIT_FLAG = 'siJdgmCarouselReady';
  var CARD_W_DESKTOP = 340;
  var CARD_W_MOBILE = 280;
  var GAP = 16;

  function log() {
    if (!DEBUG) return;
    var args = ['[si-jdgm-carousel]'].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  function isMobile() { return window.innerWidth < 750; }
  function cardWidth() { return isMobile() ? CARD_W_MOBILE : CARD_W_DESKTOP; }

  function findTrack() {
    // The container holding masonry items. Walk up from a real item so we
    // don't depend on whether the container is the inner div or the outer
    // <section>.
    var firstItem = document.querySelector('.jdgm-masonry-grid__masonry-item');
    if (!firstItem) return null;
    return firstItem.parentElement;
  }

  function findGridSection(track) {
    // The outer <section class="jdgm-masonry-grid"> may carry layout sizing
    // that we also need to neutralize.
    if (!track) return null;
    return track.closest('.jdgm-masonry-grid') || track;
  }

  function neutralizeMasonry(track) {
    // Container — strip any masonry-set height, allow flex layout to size it.
    track.style.setProperty('display', 'flex', 'important');
    track.style.setProperty('flex-direction', 'row', 'important');
    track.style.setProperty('flex-wrap', 'nowrap', 'important');
    track.style.setProperty('overflow-x', 'auto', 'important');
    track.style.setProperty('overflow-y', 'hidden', 'important');
    track.style.setProperty('scroll-snap-type', 'x mandatory', 'important');
    track.style.setProperty('scroll-behavior', 'smooth', 'important');
    track.style.setProperty('-webkit-overflow-scrolling', 'touch');
    track.style.setProperty('gap', GAP + 'px', 'important');
    track.style.setProperty('padding', '8px 4px 16px', 'important');
    track.style.setProperty('margin', '8px 0', 'important');
    track.style.setProperty('height', 'auto', 'important');
    track.style.setProperty('position', 'relative', 'important');
    track.classList.add('si-jdgm-track');

    // Outer <section> — masonry sometimes sets a height there too.
    var section = findGridSection(track);
    if (section && section !== track) {
      section.style.setProperty('height', 'auto', 'important');
      section.style.setProperty('position', 'static', 'important');
    }

    // Each masonry item — kill absolute positioning + transform, set width.
    var w = cardWidth();
    Array.prototype.forEach.call(track.children, function (item) {
      if (!item.classList) return;
      if (!item.classList.contains('jdgm-masonry-grid__masonry-item')) return;
      item.style.setProperty('position', 'static', 'important');
      item.style.setProperty('top', 'auto', 'important');
      item.style.setProperty('left', 'auto', 'important');
      item.style.setProperty('transform', 'none', 'important');
      item.style.setProperty('flex', '0 0 ' + w + 'px', 'important');
      item.style.setProperty('width', w + 'px', 'important');
      item.style.setProperty('max-width', w + 'px', 'important');
      item.style.setProperty('scroll-snap-align', 'start', 'important');
      item.style.setProperty('margin', '0', 'important');
      item.classList.add('si-jdgm-card');
    });
  }

  function applyLayout(track) {
    if (!track || track.dataset[INIT_FLAG] === '1') return false;
    log('found track:', track.className || '(no class)', '— children:', track.children.length);

    neutralizeMasonry(track);

    // Wrap in positioned container so arrows can sit on the edges.
    if (track.parentNode && !track.parentNode.classList.contains('si-jdgm-carousel-wrap')) {
      var wrap = document.createElement('div');
      wrap.className = 'si-jdgm-carousel-wrap';
      track.parentNode.insertBefore(wrap, track);
      wrap.appendChild(track);

      var prev = makeBtn('prev', '‹', 'Previous reviews');
      var next = makeBtn('next', '›', 'Next reviews');
      wrap.appendChild(prev);
      wrap.appendChild(next);

      function step() {
        var first = track.querySelector('.jdgm-masonry-grid__masonry-item');
        if (!first) return cardWidth() + GAP;
        return first.getBoundingClientRect().width + GAP;
      }

      function updateBtns() {
        var max = track.scrollWidth - track.clientWidth - 1;
        prev.toggleAttribute('disabled', track.scrollLeft <= 0);
        next.toggleAttribute('disabled', track.scrollLeft >= max);
      }

      prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      next.addEventListener('click', function () { track.scrollBy({ left:  step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', updateBtns, { passive: true });
      window.addEventListener('resize', function () {
        // Re-size cards on breakpoint cross.
        var nw = cardWidth();
        Array.prototype.forEach.call(track.children, function (item) {
          if (!item.classList || !item.classList.contains('jdgm-masonry-grid__masonry-item')) return;
          item.style.setProperty('flex', '0 0 ' + nw + 'px', 'important');
          item.style.setProperty('width', nw + 'px', 'important');
          item.style.setProperty('max-width', nw + 'px', 'important');
        });
        updateBtns();
      });

      requestAnimationFrame(updateBtns);
    }

    track.dataset[INIT_FLAG] = '1';
    log('carousel layout applied to', track.children.length, 'items');
    return true;
  }

  function makeBtn(direction, glyph, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'si-jdgm-carousel-btn si-jdgm-carousel-btn--' + direction;
    b.setAttribute('aria-label', label);
    b.textContent = glyph;
    return b;
  }

  function findAndApply() {
    var track = findTrack();
    if (track) return applyLayout(track);
    return false;
  }

  // If Judge.me re-runs masonry after we've initialized (sort/filter/page),
  // it will re-set position/top/left/transform on items. Re-neutralize.
  function reNeutralizeIfStale() {
    var track = document.querySelector('.si-jdgm-track');
    if (!track) return;
    var item = track.querySelector('.jdgm-masonry-grid__masonry-item');
    if (!item) return;
    var pos = getComputedStyle(item).position;
    if (pos === 'absolute') {
      log('re-neutralizing masonry after Judge.me re-render');
      neutralizeMasonry(track);
    }
  }

  function start() {
    log('starting');
    if (findAndApply()) return;

    var attempts = 0;
    var MAX = 40;
    var poll = setInterval(function () {
      attempts++;
      if (findAndApply() || attempts >= MAX) {
        if (attempts >= MAX) log('giving up — Judge.me never rendered masonry items');
        clearInterval(poll);
      }
    }, 500);

    // Watch for Judge.me re-renders.
    var observer = new MutationObserver(function () {
      if (!findAndApply()) reNeutralizeIfStale();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    setTimeout(function () { observer.disconnect(); }, 30000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
